import { KanbanIcone } from '../../../shared/services/models/vendas/kanban-icone.model';
import { TipoEtapaKanban } from '../../../shared/services/models/vendas/tipo-etapa-kanban.model';
import { TipoEtapaKanbanEnum } from '../../../shared/enums/vendas/tipo-etapa-kanban.enum';
import { VendaTipoDocumentoEnum } from '../../../shared/enums/vendas/venda-tipo-documento.enum';
import { TipoProcessoKanbanEnum } from '../../../shared/enums/vendas/tipo-processo-kanban.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { KanbanProcessoStruct } from '../../../shared/services/structs/vendas/kanban-processo.struct';
import { VendaDocumentoStruct } from '../../../shared/services/structs/vendas/venda-documento.struct';
import { VendaKanbanColunaStruct } from '../../../shared/services/structs/vendas/venda-kanban-coluna.struct';
import { VendaKanbanRaiaTotalStruct } from '../../../shared/services/structs/vendas/venda-kanban-raia-total.struct';
import { GetVendaKanbanResponse } from '../../../shared/services/responses/vendas/get-venda-kanban.response';
import { COR_PADRAO, PvCorVm, resolverCor } from '@shared/helpers/kanban-cor.helper';

/** Uma coluna do board. */
export interface PvColunaVm {
    idEtapaKanban: number;
    /** Identidade estável da etapa semeada pelo sistema; null quando criada pelo usuário. */
    chave: string | null;
    nome: string;
    nomeIcone: string;
    cor: PvCorVm;
    /**
     * Id da cor no catálogo `kanban_cor`, guardado ao lado da cor já resolvida.
     *
     * Existe para a troca de tema RE-RESOLVER a cor sobre a VM que está na tela, em vez de
     * remontar o board a partir da resposta do servidor — remontar descartava o card arrastado,
     * o modelo recém-criado e as páginas de "Ver mais", que só existem em memória.
     */
    idKanbanCor: number;
    /** TipoEtapaKanbanEnum: 1 FluxoPrincipal, 2 Especial, 3 EnvioRetirada. */
    idTipoEtapaKanban: number;
    ordem: number;
    /** Etapa fixa do sistema: nome e exclusão travados na configuração. */
    isFixo: boolean;
    /** Processo dono da etapa. */
    idConfigKanbanProcesso: number;
    /**
     * Para onde um card desta coluna pode ir, resolvido pelo SERVIDOR para o usuário logado.
     * A tela não conhece as regras de movimentação — só verifica se o destino está aqui.
     */
    idsEtapaDestinoPermitida: number[];
    /** Entrar nesta coluna exige motivo. */
    isMotivoObrigatorio: boolean;
    /** Motivos aceitos ao entrar aqui; vazio = qualquer motivo ativo. */
    idsMotivoTransicao: number[];
    totalRegistros: number;
    somaValorTotal: number;
    temMais: boolean;
    cards: VendaDocumentoStruct[];
    /** Estado local: a coluna começa expandida e o usuário recolhe. */
    isRecolhida: boolean;
    /** Carregando a próxima página desta coluna ("Ver mais"). */
    isCarregandoMais: boolean;
}

/**
 * Uma raia do board. O board é uma faixa horizontal de raias roláveis lateralmente, e cada
 * raia contém as suas colunas — as "colunas de colunas" que diferenciam este board do de
 * Compras, que tem um nível a menos.
 */
export interface PvRaiaVm {
    /** Identidade estável da raia, usada no track do @for e no estado de recolhimento. */
    chave: string;
    /**
     * Processo dono da raia. As faixas "Envio e retirada" e "Desfechos" herdam o do Pedido:
     * é o que permite recolher tudo que não é do processo escolhido na pílula.
     */
    idTipoProcessoKanban: number;
    nome: string;
    nomeIcone: string;
    /** Cor fixa do processo — dá a separação visual entre as raias do board. */
    cor: PvCorVm;
    /** Mesma razão do id de cor da coluna: permitir re-resolver a cor sem remontar. */
    idKanbanCor: number;
    /** Preenchido só nas raias de processo; as faixas não têm total próprio na story. */
    totalCards: number | null;
    somaValorTotal: number | null;
    /** Faixa somente leitura (Envio e retirada): exibe o selo de rotina externa. */
    isSomenteLeitura: boolean;
    textoApoio: string | null;
    colunas: PvColunaVm[];
    isRecolhida: boolean;
}

/**
 * Uma linha da visão em lista. O documento é o mesmo struct do card; o que muda é que a linha
 * precisa dos rótulos de etapa e processo já resolvidos — eles vivem na configuração, não no
 * documento, e resolver isso no template deixaria a tabela cheia de lookup.
 */
export interface PvLinhaListaVm {
    documento: VendaDocumentoStruct;
    nomeEtapa: string;
    nomeIconeEtapa: string;
    corEtapa: PvCorVm;
    nomeProcesso: string;
    nomeIconeProcesso: string;
    /** Percentual exibido ao lado da etapa quando o documento está numa faixa externa. */
    percentualFaixa: number | null;
    /**
     * Tipo da etapa onde o documento está. A Lista não desenha colunas, mas o menu de ações
     * precisa dele: "Criar modelo" só vale em etapa de fluxo principal, a mesma regra do board.
     * `null` quando a etapa não está entre as visíveis (o rótulo já cai em "—").
     */
    idTipoEtapaKanban: number | null;
}

export type PvVisao = 'kanban' | 'lista';

/**
 * Opções da pílula da toolbar.
 *
 * No Kanban ela decide QUAIS RAIAS FICAM ABERTAS — as outras recolhem, não somem. Por isso
 * "pedido" abre duas raias: o fluxo principal e os desfechos, que são etapas do próprio
 * pedido; e "envioRetirada" é uma opção própria, porque a faixa é uma raia como as outras.
 *
 * Na Lista não há raia para recolher: lá a pílula filtra por processo no servidor e
 * "envioRetirada" não é oferecida (aqueles documentos são pedidos como quaisquer outros).
 */
export type PvRecorteBoard = 'todos' | 'orcamento' | 'pedido' | 'envioRetirada';

/** Chave da faixa de rotinas externas, usada para recolher/abrir só ela. */
export const PV_CHAVE_FAIXA_ENVIO = 'faixa-envio-retirada';

/**
 * Opções da pílula "Período". São as mesmas das listagens do HUB (contas a pagar/receber),
 * e nenhuma delas passa de 3 meses de propósito: o backend recusa intervalo maior que isso
 * (VendaController.MesesMaximosDoPeriodo) para não varrer anos de venda a cada carga do board.
 */
export type PvPeriodo = 'hoje' | 'semana' | 'mes' | 'ultimos30' | 'personalizado';

/**
 * Cor de cada raia, por id do catálogo `kanban_cor`.
 *
 * É fixa de propósito: a raia é o processo, e processo não é configurável pelo usuário como a
 * etapa é — não faz sentido guardar isso no banco. Os ids saem do protótipo, que usa a mesma
 * paleta: Orçamento amarelo, Pedido vermelho, Separação/Expedição turquesa.
 *
 * A faixa de desfechos NÃO tem cor própria: ela é feita das etapas especiais do Pedido, então
 * usa a cor do Pedido. Cor diferente ali leria como um terceiro processo, que não existe.
 *
 * Resolver pelo catálogo em vez de hex literal dá o dark mode de graça: o `kanban_cor` traz os
 * dois trios, e o `resolverCor` escolhe o do tema vigente.
 */
const COR_RAIA_ORCAMENTO = 4; // Amarelo
const COR_RAIA_PEDIDO = 5; // Vermelho
const COR_RAIA_ENVIO_RETIRADA = 8; // Turquesa

/**
 * Monta as raias do board a partir da resposta da API.
 *
 * A ordem e o agrupamento seguem a story:
 *   1. Orçamento        — fluxo principal + a coluna especial Modelo, na mesma raia
 *   2. Pedido de venda  — só o fluxo principal
 *   3. Envio e retirada — marcadores das rotinas externas do Pedido
 *   4. Desfechos        — etapas especiais do Pedido
 *
 * Note a assimetria deliberada: a etapa especial do Orçamento (Modelo) fica DENTRO da raia
 * dele, enquanto os especiais do Pedido viram uma faixa própria. É o que a story descreve —
 * um catálogo de modelos não é um desfecho.
 *
 * Nome e ícone das faixas saem de `tiposEtapaKanban` (lookup tipo_etapa_kanban), não de texto fixo.
 */
export function montarRaias(
    resposta: GetVendaKanbanResponse,
    isDarkMode: boolean,
    recolhidas: Set<string>,
    colunasRecolhidas: Set<number>,
): PvRaiaVm[] {
    const raias: PvRaiaVm[] = [];

    const colunaPorEtapa = new Map<number, VendaKanbanColunaStruct>(
        (resposta.colunas ?? []).map(c => [c.idEtapaKanban, c]),
    );

    const totalPorProcesso = new Map<number, VendaKanbanRaiaTotalStruct>(
        (resposta.raias ?? []).map(r => [r.idConfigKanbanProcesso, r]),
    );

    const construirColunas = (processo: KanbanProcessoStruct, tipos: number[]): PvColunaVm[] =>
        processo.etapas
            .filter(s => tipos.includes(s.idTipoEtapaKanban))
            .sort((a, b) => a.ordem - b.ordem)
            .map(s => {
                const coluna = colunaPorEtapa.get(s.idEtapaKanban);
                const icone = (resposta.icones ?? []).find(i => i.idKanbanIcone === s.idKanbanIcone);

                return {
                    idEtapaKanban: s.idEtapaKanban,
                    chave: s.chave,
                    nome: s.nome,
                    nomeIcone: icone?.nomeIcone ?? 'label',
                    cor: resolverCor(s.idKanbanCor, resposta.cores ?? [], isDarkMode),
                    idKanbanCor: s.idKanbanCor,
                    idTipoEtapaKanban: s.idTipoEtapaKanban,
                    ordem: s.ordem,
                    isFixo: s.isFixo,
                    idConfigKanbanProcesso: s.idConfigKanbanProcesso,
                    idsEtapaDestinoPermitida: coluna?.idsEtapaDestinoPermitida ?? [],
                    isMotivoObrigatorio: s.isMotivoObrigatorio,
                    idsMotivoTransicao: s.idsMotivoTransicao ?? [],
                    totalRegistros: coluna?.totalRegistros ?? 0,
                    somaValorTotal: coluna?.somaValorTotal ?? 0,
                    temMais: coluna?.temMais ?? false,
                    cards: coluna?.cards ?? [],
                    isRecolhida: colunasRecolhidas.has(s.idEtapaKanban),
                    isCarregandoMais: false,
                };
            });

    const tipoEtapa = (id: number): TipoEtapaKanban | undefined =>
        (resposta.tiposEtapaKanban ?? []).find(t => t.idTipoEtapaKanban === id);

    for (const processo of resposta.processos ?? []) {
        const ehOrcamento = processo.idTipoProcessoKanban === TipoProcessoKanbanEnum.Orcamento;

        // No Orçamento o especial (Modelo) mora na própria raia; no Pedido ele vira faixa.
        const tiposDaRaia = ehOrcamento
            ? [TipoEtapaKanbanEnum.FluxoPrincipal, TipoEtapaKanbanEnum.Especial]
            : [TipoEtapaKanbanEnum.FluxoPrincipal];

        const colunas = construirColunas(processo, tiposDaRaia);
        if (colunas.length === 0) continue;

        const chave = `processo-${processo.idConfigKanbanProcesso}`;
        const total = totalPorProcesso.get(processo.idConfigKanbanProcesso);

        raias.push({
            chave,
            idTipoProcessoKanban: processo.idTipoProcessoKanban,
            nome: processo.descricaoTipo,
            nomeIcone: ehOrcamento ? 'request_quote' : 'shopping_cart',
            cor: resolverCor(
                ehOrcamento ? COR_RAIA_ORCAMENTO : COR_RAIA_PEDIDO,
                resposta.cores ?? [],
                isDarkMode,
            ),
            idKanbanCor: ehOrcamento ? COR_RAIA_ORCAMENTO : COR_RAIA_PEDIDO,
            totalCards: total?.totalCards ?? 0,
            somaValorTotal: total?.somaValorTotal ?? 0,
            isSomenteLeitura: false,
            textoApoio: null,
            colunas,
            isRecolhida: recolhidas.has(chave),
        });

        if (ehOrcamento) continue;

        // Faixas do Pedido de Venda, na ordem da story: rotinas externas e depois desfechos.
        const faixas: { tipo: number; chave: string; somenteLeitura: boolean; idCor: number }[] = [
            {
                tipo: TipoEtapaKanbanEnum.EnvioRetirada,
                chave: PV_CHAVE_FAIXA_ENVIO,
                somenteLeitura: true,
                idCor: COR_RAIA_ENVIO_RETIRADA,
            },
            {
                tipo: TipoEtapaKanbanEnum.Especial,
                chave: 'faixa-desfechos',
                somenteLeitura: false,
                idCor: COR_RAIA_PEDIDO,
            },
        ];

        for (const faixa of faixas) {
            const colunasFaixa = construirColunas(processo, [faixa.tipo]);
            if (colunasFaixa.length === 0) continue;

            const meta = tipoEtapa(faixa.tipo);

            raias.push({
                chave: faixa.chave,
                idTipoProcessoKanban: processo.idTipoProcessoKanban,
                nome: meta?.descricao ?? '',
                nomeIcone: meta?.nomeIcone ?? 'label',
                cor: resolverCor(faixa.idCor, resposta.cores ?? [], isDarkMode),
                idKanbanCor: faixa.idCor,
                // Faixa não soma: o total do processo já contabiliza estes documentos, e repetir
                // aqui daria a impressão de dois somatórios independentes no mesmo board.
                totalCards: null,
                somaValorTotal: null,
                isSomenteLeitura: faixa.somenteLeitura,
                textoApoio: meta?.textoApoio ?? null,
                colunas: colunasFaixa,
                isRecolhida: recolhidas.has(faixa.chave),
            });
        }
    }

    return raias;
}

/** Um card solto numa coluna de destino. */
export interface PvMovimentoCard {
    card: VendaDocumentoStruct;
    destino: PvColunaVm;
}

/**
 * "Orçamento nº 9004" / "Modelo nº M-1" — identidade do documento em uma linha.
 *
 * Serve cabeçalho de modal e mensagem de toast, onde só o número não diz de que documento se
 * trata. O rótulo do tipo é fixo no front porque o enum já é: são três valores que a
 * configuração não altera.
 */
export function rotuloDocumento(numero: number, idVendaTipoDocumento: number): string {
    const tipos: Record<number, string> = {
        [VendaTipoDocumentoEnum.Orcamento]: 'Orçamento',
        [VendaTipoDocumentoEnum.PedidoDeVenda]: 'Pedido de venda',
        [VendaTipoDocumentoEnum.Modelo]: 'Modelo',
    };

    const tipo = tipos[idVendaTipoDocumento] ?? 'Documento';

    return `${tipo} nº ${formatarNumeroDocumento(numero, idVendaTipoDocumento)}`;
}

/**
 * Número como o usuário lê.
 *
 * Modelo ganha o prefixo "M-" (#M-1). O prefixo é APRESENTAÇÃO: a coluna no banco é int e
 * continua int, com sequência própria para o tipo Modelo. Guardar "M-1" ali transformaria um
 * número em texto e quebraria ordenação e o cálculo do próximo número.
 */
export function formatarNumeroDocumento(numero: number, idVendaTipoDocumento: number): string {
    if (idVendaTipoDocumento !== VendaTipoDocumentoEnum.Modelo) return `${numero}`;

    return `M-${numero}`;
}

/** RX-05: sem Visualizar do CRM, todos os itens de oportunidade aparecem desativados com este tooltip. */
export const TOOLTIP_SEM_PERMISSAO_CRM =
    'Sem permissão para visualizar o CRM. Solicite ao administrador a permissão Visualizar do CRM.';

/** RX-06: oportunidade vinculada fora do escopo de quem vê o documento. */
export const TOOLTIP_OPORTUNIDADE_OUTRO_VENDEDOR = 'Oportunidade de outro vendedor, fora do seu escopo.';

/** RN-15: complemento da confirmação de exclusão quando o documento tem oportunidade vinculada (board e documento). */
export const PV_TEXTO_EXCLUSAO_COM_OPORTUNIDADE =
    'O vínculo com a oportunidade também é removido e o evento fica no histórico da oportunidade.';

/**
 * Permissões das ações de oportunidade do documento (RX-04, RX-05), resolvidas uma vez pela tela e usadas
 * pelo card, pela linha da lista e pelo menu Ações do documento. Motivo vazio = ação liberada.
 */
export interface PvPermissoesOportunidade {
    isVisualizarCrm: boolean;
    /** Vincular e desvincular: Alterar do CRM + Alterar do Processo de Venda. */
    motivoBloqueioVinculo: string;
    /** Criar oportunidade: Inserir do CRM + Visualizar do Processo de Venda. */
    motivoBloqueioCriacao: string;
}

export function resolverPermissoesOportunidade(listaPermissoes: number[] | null | undefined): PvPermissoesOportunidade {
    const permissoes = listaPermissoes ?? [];

    // O tooltip nomeia só a permissão que falta, no nome que o administrador vê no perfil.
    const motivo = (acao: string, exigidas: { id: PermissaoEnum; nome: string }[]): string => {
        const faltando = exigidas.filter(p => !permissoes.includes(p.id)).map(p => p.nome);
        if (!faltando.length) return '';

        return faltando.length === 1
            ? `Sem permissão para ${acao}. Solicite ao administrador a permissão ${faltando[0]}.`
            : `Sem permissão para ${acao}. Solicite ao administrador as permissões ${faltando.join(' e ')}.`;
    };

    return {
        isVisualizarCrm: permissoes.includes(PermissaoEnum.Vendas_Crm_Visualizar),
        motivoBloqueioVinculo: motivo('vincular ou desvincular o documento', [
            { id: PermissaoEnum.Vendas_Crm_Alterar, nome: 'Alterar do CRM' },
            { id: PermissaoEnum.Vendas_ProcessoVenda_Alterar, nome: 'Alterar do Processo de Venda' },
        ]),
        motivoBloqueioCriacao: motivo('criar oportunidade', [
            { id: PermissaoEnum.Vendas_Crm_Inserir, nome: 'Inserir do CRM' },
            { id: PermissaoEnum.Vendas_ProcessoVenda_Visualizar, nome: 'Visualizar do Processo de Venda' },
        ]),
    };
}

/** Campos de oportunidade que o card, a linha e o documento têm em comum (VendaDocumentoStruct e VendaDetalheStruct). */
export type PvDocumentoComOportunidade = {
    idOportunidade?: number | null;
    codigoOportunidade?: string | null;
    tituloOportunidade?: string | null;
    nomeEtapaOportunidade?: string | null;
    isOportunidadeForaDoEscopo?: boolean | null;
};

/**
 * RX-07: a marca "#N" só aparece com o resumo do CRM, que o orquestrador só preenche para quem tem Visualizar do
 * CRM. Fora do escopo aparece sem o número (RX-06, sem expor dados).
 */
export function temMarcaOportunidade(documento: PvDocumentoComOportunidade): boolean {
    return documento.idOportunidade != null && (documento.isOportunidadeForaDoEscopo === true || !!documento.codigoOportunidade);
}

/**
 * Rótulo e motivo de bloqueio de cada item de oportunidade do menu (D3), iguais no card, na linha da lista e no
 * menu Ações do documento. Motivo vazio = item habilitado. Sem Visualizar do CRM, todos ficam desativados com o
 * tooltip da RX-05 e sem o número da oportunidade.
 */
export interface PvAcoesOportunidadeVm {
    rotuloVer: string;
    motivoVer: string;
    motivoDesvincular: string;
    motivoCriar: string;
    motivoVincular: string;
    motivoVerDoCliente: string;
}

export function acoesOportunidade(
    documento: PvDocumentoComOportunidade,
    idPessoaCliente: number | null | undefined,
    permissoes: PvPermissoesOportunidade,
): PvAcoesOportunidadeVm {
    const semCrm = permissoes.isVisualizarCrm ? '' : TOOLTIP_SEM_PERMISSAO_CRM;

    return {
        rotuloVer: !semCrm && documento.codigoOportunidade ? `Ver oportunidade #${documento.codigoOportunidade}` : 'Ver oportunidade',
        motivoVer: semCrm,
        motivoDesvincular: semCrm || permissoes.motivoBloqueioVinculo,
        motivoCriar: semCrm || permissoes.motivoBloqueioCriacao
            || (idPessoaCliente ? '' : 'Informe o cliente do documento para criar a oportunidade.'),
        motivoVincular: semCrm || permissoes.motivoBloqueioVinculo
            || (idPessoaCliente ? '' : 'Informe o cliente do documento para vincular a uma oportunidade.'),
        motivoVerDoCliente: semCrm || (idPessoaCliente ? '' : 'Informe o cliente para ver as oportunidades.'),
    };
}

/** "Oportunidade #N: título (Etapa)"; fora do escopo, só "Oportunidade de outro vendedor". */
export function tooltipMarcaOportunidade(documento: PvDocumentoComOportunidade): string {
    if (documento.isOportunidadeForaDoEscopo) return 'Oportunidade de outro vendedor.';

    const etapa = documento.nomeEtapaOportunidade ? ` (${documento.nomeEtapaOportunidade})` : '';
    return `Oportunidade #${documento.codigoOportunidade}: ${documento.tituloOportunidade ?? ''}${etapa}.`;
}

/** Resolve o intervalo de datas da pílula "Período". */
export function intervaloDoPeriodo(periodo: PvPeriodo): { inicio: Date | null; fim: Date | null } {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    switch (periodo) {
        case 'hoje':
            return { inicio: hoje, fim: hoje };

        case 'semana': {
            const inicio = new Date(hoje);
            inicio.setDate(hoje.getDate() - hoje.getDay());
            const fim = new Date(inicio);
            fim.setDate(inicio.getDate() + 6);
            return { inicio, fim };
        }

        case 'mes': {
            const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
            return { inicio, fim };
        }

        case 'ultimos30': {
            const inicio = new Date(hoje);
            inicio.setDate(hoje.getDate() - 29);
            return { inicio, fim: hoje };
        }

        default:
            return { inicio: null, fim: null };
    }
}

/**
 * Monta as linhas da visão em lista, resolvendo etapa e processo a partir das raias já
 * construídas — reusa o trabalho de montarRaias em vez de reprocessar a configuração.
 */
export function montarLinhasLista(documentos: VendaDocumentoStruct[], raias: PvRaiaVm[]): PvLinhaListaVm[] {
    const colunaPorEtapa = new Map<number, { coluna: PvColunaVm; raia: PvRaiaVm }>();

    for (const raia of raias) {
        for (const coluna of raia.colunas) {
            colunaPorEtapa.set(coluna.idEtapaKanban, { coluna, raia });
        }
    }

    return documentos.map(documento => {
        const encontrado = colunaPorEtapa.get(documento.idEtapaKanban);

        return {
            documento,
            nomeEtapa: encontrado?.coluna.nome ?? '—',
            nomeIconeEtapa: encontrado?.coluna.nomeIcone ?? 'label',
            corEtapa: encontrado?.coluna.cor ?? COR_PADRAO,
            nomeProcesso: encontrado?.raia.nome ?? '—',
            nomeIconeProcesso: encontrado?.raia.nomeIcone ?? 'label',
            // Só faz sentido mostrar progresso quando o card está mesmo numa faixa externa.
            percentualFaixa: encontrado?.raia.isSomenteLeitura ? documento.percentualProgresso : null,
            idTipoEtapaKanban: encontrado?.coluna.idTipoEtapaKanban ?? null,
        };
    });
}

/**
 * Raias fantasma do carregamento.
 *
 * Reusa o MESMO PvRaiaVm do board real de propósito: assim o esqueleto tem exatamente a
 * geometria das raias e colunas de verdade, e a tela não "pula" quando os dados chegam.
 * O que muda é só o conteúdo, que os componentes trocam por loading-box quando isSkeleton.
 */
export function criarRaiasEsqueleto(qtdRaias = 2, qtdColunas = 4): PvRaiaVm[] {
    return Array.from({ length: qtdRaias }, (_, indiceRaia) => ({
        chave: `esqueleto-${indiceRaia}`,
        idTipoProcessoKanban: 0,
        nome: '',
        nomeIcone: 'label',
        cor: COR_PADRAO,
        // Fantasma não tem cor de catálogo: a cor pertence à etapa, que ainda não chegou.
        idKanbanCor: 0,
        totalCards: null,
        somaValorTotal: null,
        isSomenteLeitura: false,
        textoApoio: null,
        isRecolhida: false,
        colunas: Array.from({ length: qtdColunas }, (_, indiceColuna) => ({
            // Id negativo: nunca colide com etapa real e deixa evidente que é fantasma.
            idEtapaKanban: -(indiceRaia * 100 + indiceColuna + 1),
            chave: null,
            nome: '',
            nomeIcone: 'label',
            cor: COR_PADRAO,
            idKanbanCor: 0,
            idTipoEtapaKanban: TipoEtapaKanbanEnum.FluxoPrincipal,
            ordem: indiceColuna,
            isFixo: false,
            idConfigKanbanProcesso: 0,
            idsEtapaDestinoPermitida: [],
            isMotivoObrigatorio: false,
            idsMotivoTransicao: [],
            totalRegistros: 0,
            somaValorTotal: 0,
            temMais: false,
            cards: [],
            isRecolhida: false,
            isCarregandoMais: false,
        })),
    }));
}
